import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Card, Input, Page, PageStatus } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useDebounceValue } from 'usehooks-ts';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import { enabledFeatureLabel } from '~/utils/enabledFeatureLabel';

type Store = CommandAdminApi.ResponseBody<'/store', 'get'>[number];

const { idColumn, nameColumn, shopifyDomainColumn, createdOnColumn } =
  sharedTableDefinitions;

const storeColumns: ColumnDef<Store>[] = [
  idColumn<Store>({ meta: { align: 'left' } }),
  nameColumn<Store>(),
  {
    accessorKey: 'enabledFeatures',
    header: 'Features',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.enabledFeatures.map((feature) => (
          <Badge key={feature} variant="info">
            {enabledFeatureLabel[feature]}
          </Badge>
        ))}
      </div>
    ),
  },
  shopifyDomainColumn<Store>(),
  createdOnColumn<Store>(),
];

export default function AllStoresPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 250);
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['stores', { searchTerm: debouncedSearchTerm }],
    queryFn: () =>
      api.stores.list({ searchTerm: debouncedSearchTerm || undefined }),
  });

  return (
    <Page title="All Stores" size="fullWidth">
      <Card>
        <Input
          id="store-search"
          label="Search stores"
          labelVisuallyHidden
          type="search"
          placeholder="Search stores by name, id or domain"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />

        {match(query)
          .with({ status: 'pending' }, () => (
            <PageStatus.Loading title="Loading stores" />
          ))
          .with({ status: 'error' }, () => (
            <PageStatus.Error
              title="Failed to load stores"
              error={query.error}
            />
          ))
          .with({ status: 'success', data: P.select() }, (data) =>
            data.length > 0 ? (
              <DataTable
                data={data}
                hidePagination
                columns={storeColumns}
                onRowClick={(store) => navigate(`${store.id}`)}
              />
            ) : (
              <DataTableStatus
                title="No stores found"
                icon="store"
                details="Please adjust your search and try again"
              />
            ),
          )
          .exhaustive()}
      </Card>
    </Page>
  );
}
